import { Box, Button, HStack, Spacer, Stack, Tag, Tooltip, useToast, VStack } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAxios } from 'context/AxiosContextProvider';
import useDsbFormVersionQuery from 'hooks/useDsbFormVersionQuery';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { AiFillCar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import AlertModal from '../../components/modals/AlertModal';
import localconfig from '../../config';
import { displayRow } from '../../helpers/general';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import { ContentCard } from '../../layout/ContentCard';

const FormVersionFieldMappingInfoCard = (props: { dsbFormVersionId: number }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dsbFormVersionId } = props;
  const dsbFormVersionQuery = useDsbFormVersionQuery(dsbFormVersionId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const axios = useAxios();
  const sendRequest = async ({
    // @ts-ignore
    url,
    method = 'put',
    dataForEndpoint = {},
    disableInvalidate = false,
  }) => {
    const axiosCall =
      method !== 'delete'
        ? // @ts-ignore
          axios[method](url, dataForEndpoint)
        : // @ts-ignore
          axios[method](url);
    return await axiosCall
      .catch((err: { response: { status: number } }) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      }) // @ts-ignore
      .then(({ data }) => {
        if (disableInvalidate) {
          return data;
        } else {
          queryClient.invalidateQueries({ queryKey: useDsbFormVersionQuery.getKey(dsbFormVersionId) });
        }
      });
  };
  if (dsbFormVersionQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard
      // @ts-ignore
      icon={<AiFillCar boxSize={10} size={'40px'} />}
      minH="fit-content"
      header={t('pages:config_forms.dsb_form_versions.title')}
      data-test-id="dsb-form-version-card"
    >
      <Stack w={'100%'} data-test-id="dsb-form-version-information">
        <Stack w="full" justifyContent="space-between">
          <HStack>
            <VStack>
              <Box>
                {displayRow({
                  label: t('pages:config_forms.version'),
                  content: dsbFormVersionQuery.data?.version,
                })}
                {displayRow({
                  label: t('pages:config_forms.table.columns.status_tag'),
                  content: (
                    <Tag mt={'1px'} variant={'solid'} size={'sm'} colorScheme={isNil(dsbFormVersionQuery.data?.published_at) ? 'gray' : 'green'}>
                      {t(dsbFormVersionQuery.data?.published_at ? 'common:published' : 'common:unpublished').toUpperCase()}
                    </Tag>
                  ),
                })}
              </Box>
            </VStack>
            <Spacer />
            <Box>
              <HStack>
                <Box>
                  {/*@ts-ignore*/}
                  {isNil(dsbFormVersionQuery.data?.published_at) ? (
                    <Box>
                      <Button
                        color={'red.600'}
                        borderColor={'red.600'}
                        //@ts-ignore
                        _hover={'pink.600'}
                        variant="outline"
                        onClick={() => {
                          NiceModal.show(AlertModal, {
                            onSubmit: () =>
                              sendRequest({
                                method: 'delete',
                                url: `${localconfig.apiBaseUrl}/op/v1/dsb-form-version/${dsbFormVersionId}`,
                              }).then(() => {
                                navigate(`/app/op/dsb-form/${dsbFormVersionQuery.data?.dsb_form_id}`);
                              }),
                            children: t('pages:config_forms.dsb_form_versions.delete.message'),
                            content: {
                              header: t('pages:config_forms.dsb_form_versions.delete.title'),
                              footer: {
                                buttons: {
                                  cancelCaption: t('common:cancel'),
                                  actionCaption: t('common:delete'),
                                },
                              },
                            },
                          });
                        }}
                      >
                        {t('common:delete')}
                      </Button>
                    </Box>
                  ) : (
                    <Tooltip label={t('pages:config_forms.field_mapping.delete_tooltip')} shouldWrapChildren>
                      <Button
                        disabled={true}
                        bgColor={'red.500'}
                        // @ts-ignore
                        _hover={'red.500'}
                        variant={'primary'}
                      >
                        {t('common:delete')}
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </HStack>
            </Box>
          </HStack>
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default FormVersionFieldMappingInfoCard;
